import { useState } from "react";
import { Reveal, Bubbles } from "@/components/site/Motion";
import { Btn } from "@/components/site/Btn";
import { Mail, CheckCircle2, ArrowRight, Users, PackageCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQ } from "@/lib/content";
import { CONTACT_EMAIL } from "@/lib/assets";

const TEAM_NEEDS = ["Sales Development", "Customer Support", "Back Office and Operations", "Information Technology", "Not sure"];
const PROJECT_NEEDS = ["Data Migration", "Application Development", "Web or Mobile Development", "Backend or Integration", "Artificial Intelligence or Automation", "Other", "Not sure"];
const MIGRATION_TYPES = ["Migration for your own business", "Migration support for customers using your platform"];

// Portable endpoint: defaults to the Netlify Function. Override with REACT_APP_ENQUIRY_ENDPOINT if needed.
const ENQUIRY_ENDPOINT = process.env.REACT_APP_ENQUIRY_ENDPOINT || "/.netlify/functions/send-enquiry";

export default function Contact() {
  const [mode, setMode] = useState(null); // 'team' | 'project'
  const [form, setForm] = useState({ name: "", email: "", company: "", teamNeed: TEAM_NEEDS[0], projectNeed: PROJECT_NEEDS[0], migrationType: MIGRATION_TYPES[0], message: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Tell us your name.";
    if (!form.email.trim()) e.email = "We need an email to reply.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "That email looks off.";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "A little more detail helps (10+ characters).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    const enquiryType = mode === "team" ? "Professional / Team requirement" : "IT Project requirement";
    const payload = {
      enquiryType,
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company.trim(),
      need: mode === "team" ? form.teamNeed : form.projectNeed,
      message: form.message.trim(),
    };
    if (mode === "project" && form.projectNeed === "Data Migration") payload.migrationType = form.migrationType;

    setSubmitting(true);
    try {
      const res = await fetch(ENQUIRY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("send-failed");
      setSent(true);
    } catch (err) {
      setSubmitError("Sorry, we couldn't send that just now. Please email info@bleusharks.com or try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setSent(false); setMode(null); setForm((f) => ({ ...f, message: "" })); setErrors({}); setSubmitError(""); };
  const inputCls = "w-full bg-white border-2 border-navy rounded-xl px-4 py-3 font-body text-navy focus:outline-none focus:ring-4 focus:ring-sky/40 transition";

  return (
    <>
      <section className="relative bg-gradient-to-b from-sky/30 to-cream pt-16 md:pt-24 pb-16 overflow-hidden" data-testid="contact-hero">
        <Bubbles count={8} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-start relative">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">Contact</p>
            <h1 className="font-display font-extrabold text-5xl md:text-6xl text-navy leading-[0.95]">How can we help?</h1>
            <p className="font-body text-lg md:text-xl text-navy/70 mt-6 max-w-lg">Pick the one that fits. We'll take it from there and figure out where BleuSharks can help.</p>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="inline-flex items-center gap-3 mt-8 bg-white border-2 border-navy rounded-full px-5 py-3 shadow-brutal font-display font-bold text-lg hover:-translate-y-1 transition-transform">
              <Mail className="w-5 h-5 text-ocean" /> {CONTACT_EMAIL}
            </a>
          </Reveal>

          <Reveal delay={0.15}>
            {sent ? (
              <div className="bg-seagreen/25 border-2 border-navy rounded-3xl p-10 shadow-brutal text-center" data-testid="contact-success">
                <CheckCircle2 className="w-16 h-16 text-navy mx-auto" />
                <h2 className="font-display font-extrabold text-3xl text-navy mt-4">Got it.</h2>
                <p className="font-body text-navy/75 mt-3">Thanks for reaching out. We'll be in touch soon.</p>
                <button onClick={reset} data-testid="contact-send-another" className="mt-6 font-body font-bold underline text-ocean">Send another message</button>
              </div>
            ) : !mode ? (
              <div className="grid gap-5" data-testid="contact-mode-select">
                <button onClick={() => setMode("team")} data-testid="mode-team" className="text-left bg-sky/15 border-2 border-navy rounded-3xl p-7 shadow-brutal hover:-translate-y-1 transition-transform">
                  <span className="w-12 h-12 grid place-items-center rounded-full bg-white border-2 border-navy"><Users className="w-6 h-6" /></span>
                  <p className="font-display font-extrabold text-2xl text-navy mt-4">Build my team</p>
                  <p className="font-body text-navy/70 mt-1">I need a capable professional to join my team.</p>
                </button>
                <button onClick={() => setMode("project")} data-testid="mode-project" className="text-left bg-yellow/25 border-2 border-navy rounded-3xl p-7 shadow-brutal hover:-translate-y-1 transition-transform">
                  <span className="w-12 h-12 grid place-items-center rounded-full bg-white border-2 border-navy"><PackageCheck className="w-6 h-6" /></span>
                  <p className="font-display font-extrabold text-2xl text-navy mt-4">Discuss an IT project</p>
                  <p className="font-body text-navy/70 mt-1">I want BleuSharks to take on and deliver a project.</p>
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="bg-white border-2 border-navy rounded-3xl p-6 md:p-8 shadow-brutal-lg space-y-5" data-testid="contact-form">
                <button type="button" onClick={() => setMode(null)} data-testid="contact-back" className="font-body text-sm font-bold text-ocean underline">← Change</button>
                <p className="font-display font-bold text-xl text-navy">{mode === "team" ? "Build my team" : "Discuss an IT project"}</p>

                <div>
                  <label className="font-body font-bold text-sm text-navy">Your name</label>
                  <input value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Cooper" data-testid="contact-name" />
                  {errors.name && <p className="text-coral font-body text-sm mt-1" data-testid="error-name">{errors.name}</p>}
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body font-bold text-sm text-navy">Email</label>
                    <input value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@company.com" data-testid="contact-email" />
                    {errors.email && <p className="text-coral font-body text-sm mt-1" data-testid="error-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="font-body font-bold text-sm text-navy">Company <span className="text-navy/40">(optional)</span></label>
                    <input value={form.company} onChange={set("company")} className={inputCls} placeholder="Acme Inc." data-testid="contact-company" />
                  </div>
                </div>

                {mode === "team" ? (
                  <div>
                    <label className="font-body font-bold text-sm text-navy">What kind of professional do you need?</label>
                    <select value={form.teamNeed} onChange={set("teamNeed")} className={inputCls} data-testid="contact-team-need">
                      {TEAM_NEEDS.map((n) => <option key={n}>{n}</option>)}
                    </select>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="font-body font-bold text-sm text-navy">What do you need help with?</label>
                      <select value={form.projectNeed} onChange={set("projectNeed")} className={inputCls} data-testid="contact-project-need">
                        {PROJECT_NEEDS.map((n) => <option key={n}>{n}</option>)}
                      </select>
                    </div>
                    {form.projectNeed === "Data Migration" && (
                      <div data-testid="contact-migration-type-wrap">
                        <label className="font-body font-bold text-sm text-navy">Is this…</label>
                        <select value={form.migrationType} onChange={set("migrationType")} className={inputCls} data-testid="contact-migration-type">
                          {MIGRATION_TYPES.map((n) => <option key={n}>{n}</option>)}
                        </select>
                      </div>
                    )}
                  </>
                )}

                <div>
                  <label className="font-body font-bold text-sm text-navy">Tell us more</label>
                  <textarea value={form.message} onChange={set("message")} rows={4} className={inputCls} placeholder="What's sitting on your team's plate right now?" data-testid="contact-message" />
                  {errors.message && <p className="text-coral font-body text-sm mt-1" data-testid="error-message">{errors.message}</p>}
                </div>
                {submitError && <p className="text-coral font-body text-sm" data-testid="submit-error">{submitError}</p>}
                <Btn type="submit" variant="yellow" testid="contact-submit" className={`w-full ${submitting ? "opacity-70 pointer-events-none" : ""}`}>{submitting ? "Sending…" : "Send it over"} {!submitting && <ArrowRight className="w-5 h-5" />}</Btn>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal><h2 className="font-display font-bold text-4xl md:text-5xl text-navy text-center">Questions? We've got answers.</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 space-y-4" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-white border-2 border-navy rounded-2xl px-6 shadow-brutal" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="font-display font-bold text-lg text-navy text-left hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="font-body text-navy/70 text-base pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
